/**
 * WorkerTable component
 * Displays production metrics grouped by worker/operator
 * Includes pace (lbs/hour) for productivity comparison
 */
import React, { useState } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { WorkerSummary } from '../../../../services/productionService';
import { ChevronUp, ChevronDown, Trophy } from 'lucide-react';

interface WorkerTableProps {
  workers: WorkerSummary[];
}

type SortField = 'worker_name' | 'employee_number' | 'sessions_worked' | 'boxes_packed' | 'total_weight' | 'avg_pace';
type SortDirection = 'asc' | 'desc';

const WorkerTable: React.FC<WorkerTableProps> = ({ workers }) => {
  const { t } = useLanguage();
  const [sortField, setSortField] = useState<SortField>('total_weight');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedWorkers = [...workers].sort((a, b) => {
    let aVal = a[sortField] ?? '';
    let bVal = b[sortField] ?? '';

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal as string).toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Find top performer by pace (for highlighting)
  const topPace = workers.length > 0 ? Math.max(...workers.map(w => w.avg_pace)) : 0;

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  const getPaceColor = (pace: number) => {
    if (pace === topPace && pace > 0) return 'text-green-600 dark:text-green-400 font-bold';
    if (pace >= topPace * 0.8) return 'text-green-600 dark:text-green-400';
    if (pace >= topPace * 0.6) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  if (workers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t('endOfDaySummary.noData')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900/50">
          <tr>
            <th
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('worker_name')}
            >
              {t('endOfDaySummary.workerName')}
              <SortIcon field="worker_name" />
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('employee_number')}
            >
              {t('endOfDaySummary.employeeNumber')}
              <SortIcon field="employee_number" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('sessions_worked')}
            >
              {t('endOfDaySummary.sessionsCount')}
              <SortIcon field="sessions_worked" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('boxes_packed')}
            >
              {t('endOfDaySummary.boxesPacked')}
              <SortIcon field="boxes_packed" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('total_weight')}
            >
              {t('endOfDaySummary.weight')}
              <SortIcon field="total_weight" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('avg_pace')}
            >
              {t('endOfDaySummary.pace')} ({t('endOfDaySummary.paceUnit')})
              <SortIcon field="avg_pace" />
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedWorkers.map((worker, index) => (
            <tr key={worker.employee_number} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/30'}>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                {worker.worker_name || '-'}
                {worker.avg_pace === topPace && topPace > 0 && (
                  <span title={t('endOfDaySummary.topPerformer')}>
                    <Trophy className="h-4 w-4 text-yellow-500" />
                  </span>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {worker.employee_number}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                {worker.sessions_worked}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                {worker.boxes_packed.toLocaleString()}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                {worker.total_weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb
              </td>
              <td className={`px-4 py-3 whitespace-nowrap text-right text-sm ${getPaceColor(worker.avg_pace)}`}>
                {worker.avg_pace.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WorkerTable;
